import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  LinkIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  XMarkIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import securityScanService, { SecurityScan, SecurityFinding } from '../../services/api/security-scan.service';

export default function SecurityScanDetailsPage() {
  const { t } = useTranslation('security');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState<SecurityScan | null>(null);
  const [findings, setFindings] = useState<SecurityFinding[]>([]);
  const [selectedFinding, setSelectedFinding] = useState<SecurityFinding | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  useEffect(() => {
    if (id) {
      loadData();
      // Refresh every 10 seconds if running
      const interval = setInterval(() => {
        if (scan?.status === 'running') {
          loadData();
        }
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      const [scanRes, findingsRes] = await Promise.all([
        securityScanService.getById(id),
        securityScanService.getFindings(id),
      ]);

      if (scanRes.success) setScan(scanRes.data);
      if (findingsRes.success) setFindings(findingsRes.data);
    } catch (error) {
      console.error('Error loading scan details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
      case 'high':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 'medium':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const styles: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      info: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
    };
    return styles[severity] || styles.info;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      running: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
    };
    return styles[status] || styles.pending;
  };

  const getFindingStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      open: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      confirmed: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      false_positive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
      fixed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return styles[status] || styles.open;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const filteredFindings = severityFilter === 'all'
    ? findings
    : findings.filter(f => f.severity === severityFilter);

  const handleExportPDF = async () => {
    if (!scan) return;
    try {
      toast.loading(t('messages.exportingPDF'), { id: 'export-pdf' });
      const blob = await securityScanService.exportPDF(scan.id);
      const filename = `security-report-${scan.type}-${new Date().toISOString().split('T')[0]}.pdf`;
      securityScanService.downloadBlob(blob, filename);
      toast.success(t('messages.exportSuccess'), { id: 'export-pdf' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('messages.exportError'), { id: 'export-pdf' });
    }
  };

  const handleExportExcel = async () => {
    if (!scan) return;
    try {
      toast.loading(t('messages.exportingExcel'), { id: 'export-excel' });
      const blob = await securityScanService.exportExcel(scan.id);
      const filename = `security-report-${scan.type}-${new Date().toISOString().split('T')[0]}.xlsx`;
      securityScanService.downloadBlob(blob, filename);
      toast.success(t('messages.exportSuccess'), { id: 'export-excel' });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(t('messages.exportError'), { id: 'export-excel' });
    }
  };

  const handleCancelScan = async () => {
    if (!scan) return;
    const toastId = toast.loading('Cancelando scan...');
    try {
      const result = await securityScanService.cancel(scan.id);
      if (result.success) {
        toast.success('Scan cancelado!', { id: toastId });
        loadData();
      } else {
        toast.error('Falha ao cancelar', { id: toastId });
      }
    } catch (error: any) {
      console.error('Error cancelling scan:', error);
      toast.error(error?.message || 'Erro ao cancelar scan', { id: toastId });
    }
  };

  const handleRescan = () => {
    if (!scan) return;
    // Navigate to dashboard with scan data to open modal pre-filled
    navigate('/security', {
      state: {
        openScanModal: true,
        prefillData: {
          target: scan.target,
          type: scan.type,
          config: scan.config,
        },
      },
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  if (!scan) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('scanNotFound')}</h2>
          <button
            onClick={() => navigate('/security')}
            className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            {t('backToSecurity')}
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/security')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex-shrink-0">
                <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                  {scan.target}
                </h1>
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{t(`scanTypes.${scan.type}`)}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(scan.status)}`}>
                    {t(`status.${scan.status}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {(scan.status === 'running' || scan.status === 'pending') && (
              <button
                onClick={handleCancelScan}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
                <span className="hidden sm:inline">{t('actions.cancel')}</span>
              </button>
            )}
            {(scan.status === 'completed' || scan.status === 'failed' || scan.status === 'cancelled') && (
              <>
                <button
                  onClick={handleRescan}
                  className="flex items-center space-x-2 px-4 py-2 bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-lg hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition-colors"
                  title={t('actions.rescan') || 'Refazer análise'}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('actions.rescan') || 'Refazer'}</span>
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center space-x-2 px-4 py-2 bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 rounded-lg hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors"
                >
                  <DocumentArrowDownIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">PDF</span>
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                >
                  <TableCellsIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Excel</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Progress for running scans */}
        {scan.status === 'running' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('scanProgress')}</span>
              <span className="text-sm text-gray-500">{scan.progress}%</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full transition-all duration-500"
                style={{ width: `${scan.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Authentication Error Banner */}
        {findings.some(f => f.category === 'Authentication' && f.title?.includes('Authentication Failed')) && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                  {t('authError.title') || 'Falha na Autenticação'}
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                  {findings.find(f => f.category === 'Authentication' && f.title?.includes('Authentication Failed'))?.description ||
                    t('authError.description') || 'Não foi possível realizar login com as credenciais fornecidas. As áreas autenticadas não foram escaneadas.'}
                </p>
                <button
                  onClick={handleRescan}
                  className="mt-3 inline-flex items-center space-x-2 px-3 py-1.5 bg-orange-100 dark:bg-orange-800/50 text-orange-700 dark:text-orange-300 rounded-lg text-sm font-medium hover:bg-orange-200 dark:hover:bg-orange-800 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  <span>{t('actions.rescanWithNewCredentials') || 'Refazer com novas credenciais'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center space-x-2 mb-2">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('severity.critical')}</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{scan.critical_count}</span>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center space-x-2 mb-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('severity.high')}</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{scan.high_count}</span>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center space-x-2 mb-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('severity.medium')}</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{scan.medium_count}</span>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center space-x-2 mb-2">
              <InformationCircleIcon className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('severity.low')}</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{scan.low_count}</span>
          </div>
        </div>

        {/* Scan Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('scanDetails')}</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('startedAt')}</span>
                <div className="font-medium text-gray-900 dark:text-white">{formatDate(scan.started_at)}</div>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('completedAt')}</span>
                <div className="font-medium text-gray-900 dark:text-white">{formatDate(scan.completed_at)}</div>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('duration')}</span>
                <div className="font-medium text-gray-900 dark:text-white">{formatDuration(scan.duration_seconds)}</div>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('score')}</span>
                <div className={`font-bold text-xl ${
                  Number(scan.security_score || 0) >= 80 ? 'text-green-600' :
                  Number(scan.security_score || 0) >= 60 ? 'text-yellow-600' :
                  Number(scan.security_score || 0) >= 40 ? 'text-orange-600' :
                  'text-red-600'
                }`}>
                  {scan.security_score != null ? Number(scan.security_score).toFixed(1) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Crawl & Auth Summary - Only for pentest scans */}
        {scan.type === 'pentest' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('crawlSummary.title') || 'Resumo do Crawling'}
              </h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* URLs Discovered */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <LinkIcon className="h-5 w-5 text-cyan-500" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('crawlSummary.urlsDiscovered') || 'URLs Descobertas'}
                    </span>
                  </div>
                  {(() => {
                    const crawlFinding = findings.find(f => f.title === 'Web Crawling Results');
                    const authFinding = findings.find(f => f.title === 'Authenticated Areas Discovered');
                    let publicUrls = 0;
                    let authUrls = 0;
                    try {
                      if (crawlFinding?.evidence) {
                        const data = JSON.parse(crawlFinding.evidence);
                        publicUrls = data.urls || data.urlsDiscovered || 0;
                      }
                      if (authFinding?.evidence) {
                        const data = JSON.parse(authFinding.evidence);
                        authUrls = data.newUrls || 0;
                      }
                    } catch {}
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {t('crawlSummary.publicUrls') || 'URLs Públicas'}
                          </span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white">{publicUrls}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {t('crawlSummary.authUrls') || 'URLs Autenticadas'}
                          </span>
                          <span className={`text-lg font-bold ${authUrls > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {authUrls > 0 ? `+${authUrls}` : '0'}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</span>
                            <span className="text-lg font-bold text-cyan-600">{publicUrls + authUrls}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Forms Discovered */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <DocumentTextIcon className="h-5 w-5 text-purple-500" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('crawlSummary.formsDiscovered') || 'Formulários'}
                    </span>
                  </div>
                  {(() => {
                    // Get forms from crawler
                    const crawlFinding = findings.find(f => f.title === 'Web Crawling Results');
                    const loginFormFindings = findings.filter(f => f.title === 'Login Form Detected');
                    // Get forms scanned during vulnerability testing
                    const vulnScanComplete = findings.find(f => f.title === 'Vulnerability Scan Complete');

                    let crawlerForms = 0;
                    let scannedForms = 0;
                    let loginForms = loginFormFindings.length;

                    try {
                      if (crawlFinding?.evidence) {
                        const data = JSON.parse(crawlFinding.evidence);
                        crawlerForms = data.forms || data.formsDiscovered || 0;
                      }
                      if (vulnScanComplete?.evidence) {
                        const data = JSON.parse(vulnScanComplete.evidence);
                        scannedForms = data.formsScanned || 0;
                      }
                    } catch {}

                    // Use the higher number (vulnerability scanner extracts forms from each page)
                    const totalForms = Math.max(crawlerForms, scannedForms);

                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {t('crawlSummary.totalForms') || 'Total de Forms'}
                          </span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white">{totalForms}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {t('crawlSummary.loginForms') || 'Forms de Login'}
                          </span>
                          <span className="text-lg font-bold text-purple-600">{loginForms}</span>
                        </div>
                        {scannedForms > 0 && crawlerForms === 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-600">
                            Forms extraídos durante scan (SPA)
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Authentication Status */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <LockClosedIcon className="h-5 w-5 text-amber-500" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('crawlSummary.authStatus') || 'Autenticação'}
                    </span>
                  </div>
                  {(() => {
                    const authSuccess = findings.find(f => f.title === 'Authenticated Scan Performed');
                    const authFailed = findings.find(f => f.title?.includes('Authentication Failed'));
                    const noAuthConfigured = !scan.config?.authentication?.credentials;

                    if (noAuthConfigured) {
                      return (
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {t('crawlSummary.noAuthConfigured') || 'Não configurada'}
                          </span>
                        </div>
                      );
                    }

                    if (authSuccess) {
                      // Parse evidence to get login method details
                      let loginMethod = 'unknown';
                      let isSPA = false;
                      let apiEndpoint = '';
                      try {
                        if (authSuccess.evidence) {
                          const data = JSON.parse(authSuccess.evidence);
                          loginMethod = data.loginMethod || 'unknown';
                          isSPA = data.isSPA || false;
                          apiEndpoint = data.apiEndpoint || '';
                        }
                      } catch {}

                      return (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                              {t('crawlSummary.authSuccess') || 'Login realizado com sucesso'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {loginMethod === 'json_api' ? (
                              <>Login via <span className="font-medium text-violet-600 dark:text-violet-400">JSON API</span> (SPA)</>
                            ) : loginMethod === 'form' ? (
                              <>Login via <span className="font-medium text-blue-600 dark:text-blue-400">Formulário HTML</span></>
                            ) : (
                              <>{t('crawlSummary.authSuccessDesc') || 'Áreas autenticadas foram escaneadas'}</>
                            )}
                          </p>
                          {apiEndpoint && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate" title={apiEndpoint}>
                              {apiEndpoint}
                            </p>
                          )}
                        </div>
                      );
                    }

                    if (authFailed) {
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                              {t('crawlSummary.authFailed') || 'Falha no login'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {authFailed.description?.substring(0, 80)}...
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <span className="text-sm text-yellow-600 dark:text-yellow-400">
                          {t('crawlSummary.authPending') || 'Pendente'}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Vulnerability Scan Summary */}
              {(() => {
                const vulnScanComplete = findings.find(f => f.title === 'Vulnerability Scan Complete');
                if (!vulnScanComplete?.evidence) return null;

                try {
                  const data = JSON.parse(vulnScanComplete.evidence);
                  return (
                    <div className="mt-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                      <h4 className="text-sm font-semibold text-violet-800 dark:text-violet-300 mb-3 flex items-center space-x-2">
                        <ShieldCheckIcon className="h-5 w-5" />
                        <span>Testes de Vulnerabilidade</span>
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">{data.pagesScanned || 0}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Páginas Escaneadas</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{data.formsScanned || 0}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Forms Testados</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{data.itemsScanned || 0}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Total de Testes</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${(data.vulnerabilitiesFound || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                            {data.vulnerabilitiesFound || 0}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Vulnerabilidades</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-violet-200 dark:border-violet-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Testes realizados:</span> SQL Injection, XSS, Command Injection, Path Traversal, SSRF, Open Redirect
                        </p>
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}
            </div>
          </div>
        )}

        {/* Findings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('findings')} ({findings.length})
              </h2>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">{t('allSeverities')}</option>
                <option value="critical">{t('severity.critical')}</option>
                <option value="high">{t('severity.high')}</option>
                <option value="medium">{t('severity.medium')}</option>
                <option value="low">{t('severity.low')}</option>
                <option value="info">{t('severity.info')}</option>
              </select>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredFindings.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {findings.length === 0 ? t('noFindings') : t('noFindingsForFilter')}
              </div>
            ) : (
              filteredFindings.map((finding) => (
                <div
                  key={finding.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedFinding(selectedFinding?.id === finding.id ? null : finding)}
                >
                  <div className="flex items-start space-x-3">
                    {getSeverityIcon(finding.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">{finding.title}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(finding.severity)}`}>
                          {t(`severity.${finding.severity}`)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getFindingStatusBadge(finding.status)}`}>
                          {t(`findingStatus.${finding.status}`)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{finding.description}</p>
                      {finding.affected_url && (
                        <div className="flex items-center space-x-1 mt-1 text-xs text-gray-400">
                          <LinkIcon className="h-3 w-3" />
                          <span className="truncate">{finding.affected_url}</span>
                        </div>
                      )}

                      {/* Expanded details */}
                      {selectedFinding?.id === finding.id && (
                        <div className="mt-4 space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('details')}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{finding.description}</p>
                          </div>

                          {finding.remediation && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('remediation')}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{finding.remediation}</p>
                            </div>
                          )}

                          {finding.evidence && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('evidence')}</h4>
                              <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded-lg overflow-x-auto">
                                {finding.evidence}
                              </pre>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                            {finding.cwe_id && (
                              <span className="flex items-center space-x-1">
                                <DocumentTextIcon className="h-4 w-4" />
                                <span>CWE: {finding.cwe_id}</span>
                              </span>
                            )}
                            {finding.cve_id && (
                              <span className="flex items-center space-x-1">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <span>CVE: {finding.cve_id}</span>
                              </span>
                            )}
                            {finding.cvss_score != null && (
                              <span className="flex items-center space-x-1">
                                <ShieldExclamationIcon className="h-4 w-4" />
                                <span>CVSS: {Number(finding.cvss_score).toFixed(1)}</span>
                              </span>
                            )}
                            <span>{finding.category}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
